import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { iegmService } from '../../services/api';
import FaixaBadge from '../../components/iegm/FaixaBadge';

const IegmISaude: React.FC = () => {
  const navigate = useNavigate();
  const { municipioAtivo } = useAuth();
  const [dados, setDados] = useState<any>(null);
  const [indicadores, setIndicadores] = useState<any[]>([]);
  const [complementar, setComplementar] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const municipioId = municipioAtivo?.id;

  const carregar = useCallback(async () => {
    if (!municipioId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [mun, inds, comp] = await Promise.all([
        iegmService.getMunicipio(municipioId),
        iegmService.getIndicadores(municipioId),
        iegmService.getISaudeComplementar(municipioId),
      ]);
      setDados(mun);
      setIndicadores(inds);
      setComplementar(comp);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados I-Saude');
    } finally {
      setLoading(false);
    }
  }, [municipioId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const isaudeIndicador = indicadores.find(
    (i) => i.indicador && i.indicador.toLowerCase().includes('saude')
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/iegm')}
          variant="outlined"
          size="small"
        >
          Voltar
        </Button>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            I-Saude — Detalhamento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {municipioAtivo?.nome || 'Selecione um municipio'}
            {dados?.ano_ref && (' — ' + dados.ano_ref)}
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!dados ? (
        <Alert severity="info">
          Municipio nao encontrado na base IEGM.
        </Alert>
      ) : (
        <>
          {/* Card I-Saude IEGM */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="overline" color="text.secondary">
                    I-Saude — IEGM
                  </Typography>
                  <Typography variant="h2" fontWeight={800} sx={{ my: 1 }}>
                    {((parseFloat(dados.pct_isaude) || 0) * 100).toFixed(1)}%
                  </Typography>
                  <FaixaBadge faixa={dados.ind_isaude} size="medium" />
                </CardContent>
              </Card>
            </Grid>

            {isaudeIndicador && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Detalhamento do Indicador
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Respostas</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {isaudeIndicador.quantidade_respostas || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Pontuadas</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {isaudeIndicador.quantidade_respostas_pontuadas || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Nota Final</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {parseFloat(isaudeIndicador.nota_final || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Nota Ajustada</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {parseFloat(isaudeIndicador.nota_ajustada || 0).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Faixa Original</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <FaixaBadge faixa={isaudeIndicador.faixa} />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Rebaixamentos</Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {isaudeIndicador.rebaixamentos || 0}
                        </Typography>
                      </Grid>
                      {isaudeIndicador.faixa_pos_rebaixamento && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Faixa Pos-Rebaixamento
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <FaixaBadge faixa={isaudeIndicador.faixa_pos_rebaixamento} />
                            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                              ({((parseFloat(isaudeIndicador.pct_indice_pos_rebaixamento) || 0) * 100).toFixed(1)}%)
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Todos os indicadores do municipio */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Todos os Indicadores IEGM
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Indicador</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Percentual</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Faixa</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Nota Final</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Respostas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Rebaixamentos</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Faixa Final</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {indicadores.map((ind) => {
                  const pct = parseFloat(ind.pct_indice) || 0;
                  return (
                    <TableRow
                      key={ind.indicador}
                      sx={{
                        backgroundColor:
                          ind.indicador?.toLowerCase().includes('saude')
                            ? 'rgba(37,99,235,0.06)'
                            : undefined,
                      }}
                    >
                      <TableCell sx={{ fontWeight: ind.indicador?.toLowerCase().includes('saude') ? 700 : 400 }}>
                        {ind.indicador}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                          <Typography variant="body2">{(pct * 100).toFixed(1)}%</Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(pct * 100, 100)}
                            sx={{
                              width: 60,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: '#e2e8f0',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                backgroundColor:
                                  pct >= 0.75 ? '#16a34a' : pct >= 0.5 ? '#eab308' : pct >= 0.25 ? '#f97316' : '#ef4444',
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <FaixaBadge faixa={ind.faixa} />
                      </TableCell>
                      <TableCell align="center">
                        {parseFloat(ind.nota_final || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="center">
                        {ind.quantidade_respostas}/{ind.quantidade_respostas_pontuadas}
                      </TableCell>
                      <TableCell align="center">{ind.rebaixamentos || 0}</TableCell>
                      <TableCell align="center">
                        <FaixaBadge faixa={ind.faixa_pos_rebaixamento || ind.faixa} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Dados complementares */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Dados Complementares de Saude
          </Typography>

          {!complementar || !complementar.disponivel ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              Dados complementares de vacinacao e DCNT nao disponiveis para este municipio.
            </Alert>
          ) : (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Vacinacao */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Vacinacao
                    </Typography>
                    {complementar.vacinacao.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem dados de vacinacao disponiveis.
                      </Typography>
                    ) : (
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Imunobiologico</TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {complementar.vacinacao.map((v: any, i: number) => (
                              <TableRow key={i}>
                                <TableCell>{v.nome}</TableCell>
                                <TableCell align="right">
                                  {(v.total || 0).toLocaleString('pt-BR')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* DCNT */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      DCNT — Doenças Crônicas Não Transmissíveis
                    </Typography>
                    {complementar.dcnt.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sem dados de DCNT disponiveis.
                      </Typography>
                    ) : (
                      <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Doenca</TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {complementar.dcnt.map((d: any, i: number) => (
                              <TableRow key={i}>
                                <TableCell>{d.nome}</TableCell>
                                <TableCell align="right">
                                  {(d.total || 0).toLocaleString('pt-BR')}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
};

export default IegmISaude;
